'use client';

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { parseBrainChart } from '@/components/chart/spec';
import type { BrainChartData } from '@/components/chart/types';
import { parseBrainPicture } from '@/components/diagram/spec';
import type { BrainPictureData } from '@/components/diagram/types';
import type { CognitionChip } from '@/core/apis/yourai/cognitionChipTypes';
import { parseTurnSources, type TurnSource } from '@/core/apis/yourai/cognitionTurnSources';
import { fetchDspyWithAuthRefresh } from '@/core/apis/yourai/fetchDspyWithAuthRefresh';
import { parseSseFrame } from '@/core/apis/yourai/parseSseFrame';
import { useRafBatch } from '@/core/apis/yourai/useRafBatch';
import {
  ATLAS_PRESENTATION_CAPABILITIES,
  type AtlasPresentationViewContext,
} from '@/core/contracts/atlas-presentation';
import { presentationDirective } from '@/core/contracts/brain-team-presentation';
import { type BrainTeamPresentationPlan } from '@/core/contracts/brain-team-presentation-plan';
import {
  type BrainTeamApprovedStoryCatalogItem,
  type BrainTeamStorySelection,
  classifyBrainTeamStorySelectionDirective,
  parseBrainTeamStorySelection,
  toApprovedStoryCatalogItem,
} from '@/core/contracts/brain-team-story-selection';
import { appSettings } from '@/settings/app-settings';

import { type BrainTeamProgramBuild, safeProgramBuild } from './brain-team-program-build';
import {
  type BrainTeamStoryProjection,
  hasBrainTeamStoryProjectionDirective,
  parseBrainTeamStoryProjection,
} from './brain-team-story-projection';
import { type CognitionTurnFailure, parseCognitionTurnFailure } from './cognition-turn-failure';
import { parseProducedAttachments } from './produced-attachments';
import { joinRoundTexts } from './round-text';

export {
  parseBrainTeamStoryProjection,
  type BrainTeamStoryClaimType,
  type BrainTeamStoryKind,
  type BrainTeamStoryProjection,
} from './brain-team-story-projection';
export { type CognitionTurnFailure, parseCognitionTurnFailure } from './cognition-turn-failure';
export {
  parseBrainTeamStorySelection,
  type BrainTeamApprovedStoryCatalogItem,
  type BrainTeamStorySelection,
} from '@/core/contracts/brain-team-story-selection';

// Re-exported so existing `import { CognitionChip } from
// '.../useCognitionAskStream'` callsites keep working without the
// duplicate-declaration risk.
export type { CognitionChip };

/** The browser's IANA timezone, or '' if the runtime won't say.
 *  Sent on session creation so the brain resolves "today" / "this morning"
 *  against the user's wall clock instead of UTC. Never throws: a missing
 *  timezone degrades precision, a thrown one would kill the session. */
export function resolveClientTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

export class CognitionStreamError extends Error {
  httpStatus: number;
  code?: string;
  activeTurnId?: string;
  constructor(message: string, httpStatus: number, extra?: { code?: string; activeTurnId?: string }) {
    super(message);
    this.name = 'CognitionStreamError';
    this.httpStatus = httpStatus;
    this.code = extra?.code;
    this.activeTurnId = extra?.activeTurnId;
  }
}

/**
 * Endpoint/session seam so the SAME streaming internals (session cache, stale
 * retry, rAF-batched rounds, tool trail, chips, attachments) can drive other
 * ask surfaces — today: Brain Team ASK (`useBrainTeamAskStream`). When omitted
 * the hook targets the cognition endpoints exactly as before.
 *
 * Callers MUST pass a referentially stable object (module constant or
 * useMemo) — it participates in hook dependency arrays.
 */
export interface AskStreamTarget {
  /** Entity driving the session (cognitionId / brainTeamId). Gates readiness + attachment-cache resets. */
  entityId?: string;
  sessionPath: string;
  streamPath: string;
  /** Full path (with any query) for DELETE end-session. */
  endSessionPath: (sessionId: string) => string;
  buildSessionBody: (threadId: string) => Record<string, unknown>;
}

/**
 * Routing metadata for a Brain Team turn — populated from the `member_routed`
 * SSE event and enriched from `turn_complete` (`delegated_cognition_*`).
 * Cognition turns never set it. v1 UI keeps the single team voice; this feeds
 * the routing shimmer and the demo debug panel only.
 */
export interface TurnRoutingInfo {
  cognitionId?: string;
  cognitionName?: string;
  isTeamLeader?: boolean;
  routingStage?: string;
  routingReason?: string;
  routingConfidence?: number;
}

export interface BrainTeamMapTarget {
  id: string;
  label: string;
  aliases?: string[];
  layer_id?: string;
  layer_label?: string;
  /** Bounded display/order hint from an authorized register row. */
  observed_at?: string;
  /** Opaque references only; render URLs remain in the browser's authorized
   * register projection and never cross the cognition request boundary. */
  media_refs?: Array<{
    ref_id: string;
    kind: 'image' | 'video' | 'document' | 'link';
    label?: string;
  }>;
}

export interface BrainTeamMapFocus {
  /** Versioned Atlas view-command metadata. Optional for legacy responses. */
  schema_version?: number | string;
  operation?: 'highlight_and_fit' | 'isolate_layers' | 'restore_all' | 'build_route';
  preserve_universe?: boolean;
  /** Exact counts can exceed the bounded target catalog sent on the request. */
  matched?: number;
  total?: number;
  target_ids: string[];
  layer_ids: string[];
  mode: 'fit_targets' | 'fit_layers' | 'itinerary';
  source: string;
  stops: Array<{
    target_id: string;
    label: string;
    action: string;
    sequence: number;
  }>;
  unresolved_stops: Array<{
    label: string;
    action: string;
    sequence: number;
  }>;
}

/** Parse only exact, bounded map-focus wire data. Unknown ids are filtered
 *  later against the browser's current authorized catalog before rendering. */
export function parseBrainTeamMapFocus(value: unknown): BrainTeamMapFocus | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const targetIds = Array.isArray(raw.target_ids)
    ? Array.from(new Set(raw.target_ids.filter((id): id is string => typeof id === 'string' && !!id))).slice(
        0,
        24,
      )
    : [];
  const layerIds = Array.isArray(raw.layer_ids)
    ? Array.from(new Set(raw.layer_ids.filter((id): id is string => typeof id === 'string' && !!id))).slice(
        0,
        8,
      )
    : [];
  const targetIdSet = new Set(targetIds);
  const stops = Array.isArray(raw.stops)
    ? raw.stops
        .filter((stop): stop is Record<string, unknown> => !!stop && typeof stop === 'object')
        .map((stop) => ({
          target_id: typeof stop.target_id === 'string' ? stop.target_id : '',
          label: typeof stop.label === 'string' ? stop.label.trim().slice(0, 120) : '',
          action: typeof stop.action === 'string' ? stop.action.trim().slice(0, 240) : '',
          sequence:
            typeof stop.sequence === 'number' && Number.isFinite(stop.sequence)
              ? Math.max(1, Math.floor(stop.sequence))
              : 0,
        }))
        .filter((stop) => stop.target_id && targetIdSet.has(stop.target_id) && stop.label && stop.sequence)
        .slice(0, 12)
    : [];
  const unresolvedStops = Array.isArray(raw.unresolved_stops)
    ? raw.unresolved_stops
        .filter((stop): stop is Record<string, unknown> => !!stop && typeof stop === 'object')
        .map((stop) => ({
          label: typeof stop.label === 'string' ? stop.label.trim().slice(0, 120) : '',
          action: typeof stop.action === 'string' ? stop.action.trim().slice(0, 240) : '',
          sequence:
            typeof stop.sequence === 'number' && Number.isFinite(stop.sequence)
              ? Math.max(1, Math.floor(stop.sequence))
              : 0,
        }))
        .filter((stop) => stop.label && stop.sequence)
        .slice(0, 12)
    : [];
  const schemaVersion =
    typeof raw.schema_version === 'number' && Number.isFinite(raw.schema_version)
      ? raw.schema_version
      : typeof raw.schema_version === 'string' && raw.schema_version.trim()
      ? raw.schema_version.trim().slice(0, 24)
      : undefined;
  const operation =
    raw.operation === 'highlight_and_fit' ||
    raw.operation === 'isolate_layers' ||
    raw.operation === 'restore_all' ||
    raw.operation === 'build_route'
      ? raw.operation
      : undefined;
  const preserveUniverse = typeof raw.preserve_universe === 'boolean' ? raw.preserve_universe : undefined;
  const exactCount = (candidate: unknown): number | undefined =>
    typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0
      ? Math.floor(candidate)
      : undefined;
  const matched = exactCount(raw.matched);
  const total = exactCount(raw.total);
  const hasViewCommand =
    schemaVersion !== undefined ||
    operation !== undefined ||
    preserveUniverse !== undefined ||
    matched !== undefined ||
    total !== undefined;
  if (!targetIds.length && !layerIds.length && !unresolvedStops.length && !hasViewCommand) return undefined;
  const itinerary = raw.mode === 'itinerary' && (!!stops.length || !!unresolvedStops.length);
  return {
    ...(schemaVersion !== undefined ? { schema_version: schemaVersion } : {}),
    ...(operation !== undefined ? { operation } : {}),
    ...(preserveUniverse !== undefined ? { preserve_universe: preserveUniverse } : {}),
    ...(matched !== undefined ? { matched } : {}),
    ...(total !== undefined ? { total } : {}),
    target_ids: targetIds,
    layer_ids: layerIds,
    mode: itinerary
      ? 'itinerary'
      : raw.mode === 'fit_layers' && !targetIds.length
      ? 'fit_layers'
      : 'fit_targets',
    source: typeof raw.source === 'string' ? raw.source : 'turn',
    stops: itinerary ? stops : [],
    unresolved_stops: itinerary ? unresolvedStops : [],
  };
}

/** A typed, server-resolved story for the Atlas to perform: exact catalog
 *  target ids in authored order, one narrated line each. Rides beside
 *  `map_focus` on `turn_complete`; ids are still re-checked against the
 *  browser's current drawn catalog before anything performs. */
export interface BrainTeamMapStory {
  schema_version?: number | string;
  source: string;
  stops: Array<{
    target_id: string;
    label: string;
    line: string;
    sequence: number;
  }>;
}

export function parseBrainTeamMapStory(value: unknown): BrainTeamMapStory | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const raw = value as Record<string, unknown>;
  const seen = new Set<string>();
  const stops = Array.isArray(raw.stops)
    ? raw.stops
        .filter((stop): stop is Record<string, unknown> => !!stop && typeof stop === 'object')
        .map((stop) => ({
          target_id: typeof stop.target_id === 'string' ? stop.target_id : '',
          label: typeof stop.label === 'string' ? stop.label.trim().slice(0, 160) : '',
          line: typeof stop.line === 'string' ? stop.line.trim().slice(0, 200) : '',
          sequence:
            typeof stop.sequence === 'number' && Number.isFinite(stop.sequence)
              ? Math.max(0, Math.floor(stop.sequence))
              : 0,
        }))
        .filter((stop) => {
          if (!stop.target_id || !stop.label || seen.has(stop.target_id)) return false;
          seen.add(stop.target_id);
          return true;
        })
        .sort((a, b) => a.sequence - b.sequence)
        .slice(0, 12)
    : [];
  if (!stops.length) return undefined;
  return {
    ...(typeof raw.schema_version === 'number' && Number.isFinite(raw.schema_version)
      ? { schema_version: raw.schema_version }
      : {}),
    source: typeof raw.source === 'string' && raw.source ? raw.source.slice(0, 48) : 'authored',
    stops,
  };
}

/** A bounded summary of an Asset-authorized, immutable approved story. DSPy
 * may use these summaries to choose a story, but never receives a draft or
 * projection and therefore cannot grant playback authority. */
interface UseCognitionAskStreamParams {
  token?: string;
  cognitionId?: string;
  /** Alternate endpoint target (Brain Team). Omit for cognition ASK. */
  target?: AskStreamTarget;
  /**
   * Embed only. Base URL for dspy cognition calls routed through the
   * same-origin widget-proxy (e.g. `${appBaseUrl}/api/widget-proxy/dspy`).
   * On external embed sites a direct call to dspy-api is cross-origin and
   * CORS-blocked, so the SSE never starts and the widget falls back to
   * polling. Routing through the proxy keeps the stream same-origin.
   *
   * When set, a plain `fetch` (bearer = `token`, plus `requestHeaders`) is
   * used instead of the cookie-session refresh wrapper — embeds have no
   * iron-session cookie. Leave undefined for all in-app callers to preserve
   * the existing behaviour exactly.
   */
  dspyBaseUrl?: string;
  /** Embed only. Extra headers the widget-proxy requires (X-Bolder-Widget…). */
  requestHeaders?: Record<string, string>;
  /**
   * Every parsed SSE frame of a turn, as it arrives and before the hook reads it — for a surface
   * that draws the turn's own steps (the knowledge city's live run). Optional; the hook's own
   * behaviour does not change with it.
   */
  onEvent?: (payload: Record<string, unknown>) => void;
}

export interface RoundState {
  round: number;
  text: string;
  kind?: 'thinking' | 'answer';
}

export interface CognitionActiveToolCall {
  name: string;
  label: string;
  progress?: number; // 0–1, only set for paginated document reads
}

export interface ToolCallRecord {
  name: string;
  label: string;
  argsSummary: string;
  resultSummary: string;
  isError: boolean;
  isDuplicate: boolean;
  /** The Python the leader ran — the trail's "Show the Python" drawer (execute_python only). */
  code?: string;
  /** What an execute_python run made: a file (its name) or only text — so the run that made a
   *  program's file is told apart from a check the leader ran after it. */
  output?: ExecutePythonOutput;
  /** A failed execute_python run's error as the sandbox gave it (the summary keeps 72 characters),
   *  so what went wrong can be said, and handed back to the leader, in full. */
  errorText?: string;
}

/** A failed execute_python run's error, whole enough to act on (bounded). */
export function executePythonError(result: unknown): string | undefined {
  if (!result || typeof result !== 'object') return undefined;
  const r = result as Record<string, unknown>;
  if (r.ok !== false) return undefined;
  const error = typeof r.error === 'string' ? r.error.trim() : '';
  return error ? error.slice(-1200) : undefined;
}

export interface ExecutePythonOutput {
  type: 'file' | 'text';
  fileName?: string;
}

/** What an execute_python result says it made (`{ ok, output_type, file_name? }`), if it says. */
export function executePythonOutput(result: unknown): ExecutePythonOutput | undefined {
  if (!result || typeof result !== 'object') return undefined;
  const r = result as Record<string, unknown>;
  if (r.ok === false) return undefined;
  if (r.output_type === 'file') {
    const fileName = typeof r.file_name === 'string' ? r.file_name.trim() : '';
    return { type: 'file', ...(fileName ? { fileName } : {}) };
  }
  if (r.output_type === 'text') return { type: 'text' };
  return undefined;
}

// Returns full concatenated text across all rounds (used for guest service / history)
export function getRoundsText(rounds: RoundState[]): string {
  return joinRoundTexts(rounds.map((r) => r.text));
}

// Returns only the answer text — rounds not tagged 'thinking'.
// Falls back to full text when no kind tags have arrived yet (streaming or single-round).
export function getAnswerText(rounds: RoundState[]): string {
  const answerRounds = rounds.filter((r) => r.kind !== 'thinking');
  return answerRounds.length > 0 ? joinRoundTexts(answerRounds.map((r) => r.text)) : getRoundsText(rounds);
}

// Returns rounds tagged as thinking (reasoning steps before tool results)
export function getThinkingRounds(rounds: RoundState[]): RoundState[] {
  return rounds.filter((r) => r.kind === 'thinking');
}

const SKIP_TOOLS = new Set([
  'show_content',
  'advance_step',
  'draw',
  'end_session',
  // Terminal tool — chips communicate the outcome; no result event is fed back
  'ask_clarifying_question',
]);

const TOOL_LIVE_LABELS: Record<string, string> = {
  cognition_kb_search: 'Searching knowledge base…',
  user_kb_search: 'Searching knowledge base…',
  board_content_search: 'Searching this board…',
  web_search: 'Searching the web…',
  get_attachment: 'Reading document…',
  search_in_attachment: 'Looking inside document…',
  // The team leader's sandbox run (leader-scoped, vendor-gated).
  execute_python: 'Running Python…',
};

function resolveToolLabel(name: string, args: Record<string, unknown>, displayName?: string): string {
  if (name === 'search_in_attachment') {
    const filename = typeof args.filename === 'string' ? args.filename : null;
    return filename ? `Looking inside ${filename}…` : 'Looking inside document…';
  }
  const known = TOOL_LIVE_LABELS[name];
  if (known) return known;
  // Tools this map has never heard of — linked MCP tools (`mcp__server__tool`)
  // among them — ship a server-authored display_name; prefer it, and never
  // fall back to the raw registry identifier: a reader only sees plain labels.
  const server = typeof displayName === 'string' ? displayName.trim() : '';
  if (server) return server.endsWith('…') ? server : `${server}…`;
  return 'Working…';
}

function toolArgsSummary(name: string, args: Record<string, unknown>): string {
  const q = typeof args.query === 'string' ? `"${args.query.slice(0, 40)}"` : null;
  switch (name) {
    case 'cognition_kb_search':
    case 'user_kb_search':
    case 'board_content_search':
    case 'web_search':
      return q ?? '';
    case 'search_in_attachment': {
      const filename = typeof args.filename === 'string' ? args.filename : '';
      return [filename, q].filter(Boolean).join(' · ');
    }
    case 'get_attachment': {
      // Never an identifier tail — only a human name when the engine gives one.
      const filename =
        typeof args.filename === 'string' ? args.filename : typeof args.name === 'string' ? args.name : '';
      return filename;
    }
    case 'execute_python': {
      // The produced file's human name when the run makes one, else what kind
      // of result comes back. Never the code itself.
      const fileName = typeof args.output_file_name === 'string' ? args.output_file_name.trim() : '';
      if (fileName) return fileName.slice(0, 48);
      const outputType = typeof args.output_type === 'string' ? args.output_type : 'text';
      const ext = typeof args.output_ext === 'string' ? args.output_ext.replace(/^\./, '').trim() : '';
      return outputType === 'file' ? `${ext || 'file'} output` : 'inline result';
    }
    default: {
      // Linked MCP tools carry arbitrary schemas; peek at the first few
      // primitive args so the trail reads "DXB · EBL · 2026-09-14", not blank.
      if (name.startsWith('mcp__')) {
        const parts: string[] = [];
        for (const value of Object.values(args)) {
          if (parts.length >= 3) break;
          if (typeof value === 'string' && value && value.length <= 24) parts.push(value);
          else if (typeof value === 'number') parts.push(String(value));
        }
        return parts.join(' · ').slice(0, 48);
      }
      return '';
    }
  }
}

const OK_RESULT = { isError: false, isDuplicate: false } as const;

export function toolResultSummary(
  name: string,
  result: unknown,
): { summary: string; isError: boolean; isDuplicate: boolean; progress?: number } {
  if (!result || typeof result !== 'object') return { ...OK_RESULT, summary: 'done' };
  const r = result as Record<string, unknown>;
  // Handlers report failure two ways: a structured `{ error: { code, message } }`
  // (knowledge and attachment tools) or a flat `{ ok: false, error: "…" }` (the
  // sandbox). The flat message is the one a reader needs verbatim — "Code-execution
  // limit reached for this business." is the difference between a bug and a top-up.
  if (typeof r.error === 'string' && r.error.trim()) {
    return { summary: r.error.trim().slice(0, 72), isError: true, isDuplicate: false };
  }
  const error = r.error as Record<string, unknown> | undefined;
  if (error) {
    const code = typeof error.code === 'string' ? error.code : '';
    if (code === 'duplicate_call') return { summary: '', isError: true, isDuplicate: true };
    const msg = typeof error.message === 'string' ? error.message : 'error';
    return { summary: msg, isError: true, isDuplicate: false };
  }
  if (name === 'execute_python') {
    // `{ ok, output_type, file_name?, text? }` — no `data` envelope. A file
    // result is also attached to the reply; the chip names it.
    if (r.ok === false) return { summary: 'failed', isError: true, isDuplicate: false };
    const fileName = typeof r.file_name === 'string' ? r.file_name.trim() : '';
    if (r.output_type === 'file') {
      return { ...OK_RESULT, summary: fileName ? `produced ${fileName.slice(0, 40)}` : 'produced a file' };
    }
    return { ...OK_RESULT, summary: 'ran' };
  }
  const data = r.data as Record<string, unknown> | undefined;
  if (!data) return { ...OK_RESULT, summary: 'done' };
  switch (name) {
    case 'cognition_kb_search':
    case 'user_kb_search':
    case 'board_content_search': {
      const count = Array.isArray(data.matches)
        ? data.matches.length
        : Array.isArray(data.results)
        ? data.results.length
        : typeof data.count === 'number'
        ? data.count
        : 0;
      return { ...OK_RESULT, summary: `${count} match${count !== 1 ? 'es' : ''}` };
    }
    case 'web_search': {
      const count = Array.isArray(data.results) ? data.results.length : 0;
      return { ...OK_RESULT, summary: `${count} result${count !== 1 ? 's' : ''}` };
    }
    case 'get_attachment': {
      if (data.attachment_type === 'portal' && typeof data.total_chars === 'number') {
        const charOffset = typeof data.char_offset === 'number' ? data.char_offset : 0;
        const charsReturned = typeof data.chars_returned === 'number' ? data.chars_returned : 0;
        const totalChars = data.total_chars;
        const progress = Math.min((charOffset + charsReturned) / totalChars, 1);
        const kbRead = Math.round((charOffset + charsReturned) / 1000);
        const kbTotal = Math.round(totalChars / 1000);
        return { ...OK_RESULT, summary: `read ${kbRead}K of ${kbTotal}K characters`, progress };
      }
      if (data.attachment_type === 'file' && typeof data.total_chunks === 'number') {
        const chunkOffset = typeof data.chunk_offset === 'number' ? data.chunk_offset : 0;
        const chunksReturned = typeof data.chunks_returned === 'number' ? data.chunks_returned : 0;
        const progress = Math.min((chunkOffset + chunksReturned) / data.total_chunks, 1);
        const readSoFar = Math.min(chunkOffset + chunksReturned, data.total_chunks);
        return {
          ...OK_RESULT,
          summary: `read ${readSoFar} of ${data.total_chunks} parts`,
          progress,
        };
      }
      return { ...OK_RESULT, summary: 'read' };
    }
    default:
      return { ...OK_RESULT, summary: 'done' };
  }
}

export interface AttachmentContentItem {
  _id?: string;
  contentType: 'image' | 'file' | 'pdf';
  content: { url?: string; fileName?: string; caption?: string };
}

interface LiveTurnState {
  id: string;
  userText: string;
  rounds: RoundState[];
  attachments?: AttachmentContentItem[];
  /** The picture the Brain drew this turn (`picture` frame) — shown the moment it lands. */
  picture?: BrainPictureData;
  /** The chart the Brain drew this turn (`chart` frame) — shown the moment it lands. */
  chart?: BrainChartData;
}

export type NextStepIntent = 'clarify' | 'explore' | 'none';

export { parseTurnSources, type TurnSource } from '@/core/apis/yourai/cognitionTurnSources';

interface CompletedTurnState {
  id: string;
  /** Authoritative terminal-envelope ID. The local id stays stable for UI identity. */
  serverTurnId?: string;
  userText: string;
  rounds: RoundState[];
  nextStepIntent: NextStepIntent;
  nextStepOptions?: CognitionChip[];
  clarifyingQuestionText?: string;
  sourcesUsed?: Array<'kb' | 'web' | 'attachment' | 'general'>;
  /** Titled sources behind this answer (`turn_complete.sources`). */
  sources?: TurnSource[];
  /** The picture the Brain drew (the `picture` frame, echoed on `turn_complete.picture`). */
  picture?: BrainPictureData;
  /** The chart the Brain drew (the `chart` frame, echoed on `turn_complete.chart`). */
  chart?: BrainChartData;
  turnDurationMs?: number;
  suggestedThreadTitle?: string;
  toolCalls?: ToolCallRecord[];
  attachments?: AttachmentContentItem[];
  /** Files this turn produced (execute_python output) — the engine attached them to the reply. */
  producedAttachments?: AttachmentContentItem[];
  /** Canonical thread message ids from `turn_complete` — used to re-ID optimistic stream messages. */
  userThreadMessageId?: string;
  botThreadMessageId?: string;
  /** Brain Team only — which member answered this turn. Undefined on cognition turns. */
  routing?: TurnRoutingInfo;
  /** Brain Team only — exact Atlas row/register targets for the completed
   *  answer and its visible next-step suggestions. */
  mapFocus?: BrainTeamMapFocus;
  mapStory?: BrainTeamMapStory;
  /** Brain Team only — a semantic, versioned Evidence Story. The Atlas UI
   * resolves its ids and evidence against the authorized register projection. */
  storyProjection?: BrainTeamStoryProjection;
  /** A non-null raw story_projection owns this turn even when strict parsing
   * rejects it. Consumers use this to fail closed instead of reviving a legacy
   * route or prose-derived tour. */
  storyProjectionDirectivePresent: boolean;
  /** Brain Team only — ID+revision pointer into the separately authorized
   * approved Story Library. It never contains projection authority. */
  storySelection?: BrainTeamStorySelection;
  /** Any non-null raw story_selection owns this turn. A release or malformed
   * value clears older approved playback without reviving legacy narration. */
  storySelectionDirectivePresent: boolean;
  /** Terminal failure stays attached to the optimistic USER row until history
   * hydrates the persisted failed status. */
  failure?: CognitionTurnFailure;
  /**
   * The stream stopped before the turn's terminal frame, with no error from the server: the
   * connection broke. The turn may still be running, and store its answer, on the server.
   */
  interrupted?: boolean;
  presentationPlan?: BrainTeamPresentationPlan;
  presentationPlanDirectivePresent?: boolean;
  presentationPersistence?: 'saved' | 'unavailable';
}

interface SendTextOptions {
  /** What the optimistic user bubble shows when the sent text carries protocol
   *  scaffolding the reader should not see (a plan-first instruction). */
  displayText?: string;
  attachmentType?: 'file' | 'image';
  attachmentIds?: string[];
  attachments?: AttachmentContentItem[];
  forceWebSearch?: boolean;
  /** Brain-team only: hand this turn to a specific member (the "get
   *  <member>'s perspective" chips). Sent as `target_cognition_id` — the BE
   *  skips routing and delegates to that member (stage `user_pick`), or 400s
   *  if the member left the roster. Ignored on single-brain targets. */
  targetCognitionId?: string;
  /** Brain-team only: presentation catalog from register rows already loaded
   *  through Asset authorization. DSPy can echo only these ids as Atlas
   *  focus hints; sending them never grants register access. */
  mapTargets?: BrainTeamMapTarget[];
  /** Brain-team only: all authorized ACTIVE register rows eligible for a
   * grounded story, including non-drawable evidence. Never changes map totals. */
  storyTargets?: BrainTeamMapTarget[];
  /** Brain-team only: exact durable Atlas size, sent separately because the
   *  target catalog is intentionally bounded. */
  mapUniverseTotal?: number;
  /** Brain-team only: bounded, approved summaries. Drafts and projections are
   * deliberately not accepted by this stream boundary. */
  approvedStoryCatalog?: BrainTeamApprovedStoryCatalogItem[];
  /** Brain-team only: exact approved story the reader is currently viewing.
   * The backend revalidates it against approvedStoryCatalog. */
  activeStorySelection?: BrainTeamStorySelection;
  /** Only a mounted, capable Atlas may supply this correlated view snapshot. */
  presentationViewContext?: AtlasPresentationViewContext;
  /** Brain-team only: this turn builds or changes a brainwave program (`BrainTeamProgramBuild`). */
  programBuild?: BrainTeamProgramBuild;
}

function safeBrainTeamTargets(value: unknown): BrainTeamMapTarget[] {
  if (!Array.isArray(value)) return [];
  return value.slice(0, 300).flatMap((candidate): BrainTeamMapTarget[] => {
    if (!candidate || typeof candidate !== 'object') return [];
    const raw = candidate as Record<string, unknown>;
    const id = typeof raw.id === 'string' && raw.id.trim().length <= 192 ? raw.id.trim() : '';
    const label = typeof raw.label === 'string' && raw.label.trim().length <= 160 ? raw.label.trim() : '';
    if (!id || !label) return [];
    const aliases = Array.isArray(raw.aliases)
      ? Array.from(
          new Set(
            raw.aliases
              .filter((item): item is string => typeof item === 'string')
              .map((item) => item.trim())
              .filter((item) => item && item.length <= 160),
          ),
        ).slice(0, 8)
      : [];
    const layerId =
      typeof raw.layer_id === 'string' && raw.layer_id.trim().length <= 96 ? raw.layer_id.trim() : '';
    const layerLabel =
      typeof raw.layer_label === 'string' && raw.layer_label.trim().length <= 160
        ? raw.layer_label.trim()
        : '';
    const observedAt =
      typeof raw.observed_at === 'string' && raw.observed_at.trim().length <= 64
        ? raw.observed_at.trim()
        : '';
    const mediaRefs = Array.isArray(raw.media_refs)
      ? raw.media_refs
          .flatMap((item): NonNullable<BrainTeamMapTarget['media_refs']> => {
            if (!item || typeof item !== 'object') return [];
            const media = item as Record<string, unknown>;
            const refId =
              typeof media.ref_id === 'string' && media.ref_id.trim().length <= 192
                ? media.ref_id.trim()
                : '';
            const kind = media.kind;
            if (!refId || !['image', 'video', 'document', 'link'].includes(String(kind))) return [];
            const mediaLabel =
              typeof media.label === 'string' && media.label.trim().length <= 160 ? media.label.trim() : '';
            return [
              {
                ref_id: refId,
                kind: kind as 'image' | 'video' | 'document' | 'link',
                ...(mediaLabel ? { label: mediaLabel } : {}),
              },
            ];
          })
          .slice(0, 8)
      : [];
    return [
      {
        id,
        label,
        ...(aliases.length ? { aliases } : {}),
        ...(layerId ? { layer_id: layerId } : {}),
        ...(layerLabel ? { layer_label: layerLabel } : {}),
        ...(observedAt ? { observed_at: observedAt } : {}),
        ...(mediaRefs.length ? { media_refs: mediaRefs } : {}),
      },
    ];
  });
}

/**
 * Prompt substituted when the user sends an attachment with no typed text.
 * The BE rejects empty-text turns ("Empty turn text"), so an attachment-only
 * send needs a non-empty prompt. Exported so callers can show the SAME text in
 * the optimistic user bubble — otherwise the bubble looks empty pre-send and
 * the persisted prompt only appears after a refresh.
 */
export function defaultAttachmentPrompt(attachmentType?: 'file' | 'image'): string {
  return attachmentType === 'image'
    ? 'Please take a look at the attached image.'
    : 'Please take a look at the attached file.';
}

export function useCognitionAskStream({
  token,
  cognitionId,
  target,
  dspyBaseUrl,
  requestHeaders,
  onEvent,
}: UseCognitionAskStreamParams) {
  const onEventRef = useRef(onEvent);
  useLayoutEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);
  const [liveTurn, setLiveTurn] = useState<LiveTurnState | null>(null);
  const [completedTurn, setCompletedTurn] = useState<CompletedTurnState | null>(null);
  const [liveToolCall, setLiveToolCall] = useState<CognitionActiveToolCall | null>(null);
  const [liveRouting, setLiveRouting] = useState<TurnRoutingInfo | null>(null);

  // Default target = the cognition endpoints, byte-for-byte the pre-seam behavior.
  const resolvedTarget = useMemo<AskStreamTarget>(
    () =>
      target ?? {
        entityId: cognitionId,
        sessionPath: '/api/v1/cognition/session',
        streamPath: '/api/v1/cognition/turn/stream',
        endSessionPath: (sessionId: string) => `/api/v1/cognition/session/${sessionId}?discard=false`,
        buildSessionBody: (threadId: string) => ({
          cognition_id: cognitionId,
          mode: 'ask',
          thread_id: threadId,
          client_timezone: resolveClientTimezone(),
        }),
      },
    [target, cognitionId],
  );
  const entityId = resolvedTarget.entityId;

  const sessionByThreadRef = useRef<Map<string, string>>(new Map());
  const activeTurnAbortRef = useRef<AbortController | null>(null);
  const liveTurnRef = useRef<LiveTurnState | null>(null);
  // Mutable in place to avoid Map-clone churn; version bumps notify consumers.
  const attachmentsByMessageIdRef = useRef<Map<string, AttachmentContentItem[]>>(new Map());
  const [attachmentsVersion, setAttachmentsVersion] = useState(0);
  const pendingAttachmentsRef = useRef<AttachmentContentItem[] | undefined>(undefined);
  const pendingRoundsRef = useRef<RoundState[]>([]);
  const pendingNextStepIntentRef = useRef<NextStepIntent>('none');
  const pendingNextStepOptionsRef = useRef<CognitionChip[] | undefined>(undefined);
  const pendingToolCallsRef = useRef<ToolCallRecord[]>([]);
  const pendingClarifyingQuestionTextRef = useRef<string | undefined>(undefined);
  const pendingSourcesUsedRef = useRef<Array<'kb' | 'web' | 'attachment'> | undefined>(undefined);
  const pendingSourcesRef = useRef<TurnSource[] | undefined>(undefined);
  const pendingPictureRef = useRef<BrainPictureData | undefined>(undefined);
  const pendingChartRef = useRef<BrainChartData | undefined>(undefined);
  const pendingTurnDurationMsRef = useRef<number | undefined>(undefined);
  const pendingSuggestedThreadTitleRef = useRef<string | undefined>(undefined);
  const pendingUserThreadMessageIdRef = useRef<string | undefined>(undefined);
  const pendingBotThreadMessageIdRef = useRef<string | undefined>(undefined);
  const pendingProducedAttachmentsRef = useRef<AttachmentContentItem[] | undefined>(undefined);
  const pendingRoutingRef = useRef<TurnRoutingInfo | undefined>(undefined);
  const pendingMapFocusRef = useRef<BrainTeamMapFocus | undefined>(undefined);
  const pendingMapStoryRef = useRef<BrainTeamMapStory | undefined>(undefined);
  const pendingStoryProjectionRef = useRef<BrainTeamStoryProjection | undefined>(undefined);
  const pendingStoryProjectionDirectivePresentRef = useRef(false);
  const pendingStorySelectionRef = useRef<BrainTeamStorySelection | undefined>(undefined);
  const pendingStorySelectionDirectivePresentRef = useRef(false);
  const pendingTurnFailureRef = useRef<CognitionTurnFailure | undefined>(undefined);
  const pendingInterruptedRef = useRef(false);
  const pendingServerTurnIdRef = useRef<string | undefined>(undefined);
  const pendingPresentationPlanRef = useRef<BrainTeamPresentationPlan | undefined>(undefined);
  const pendingPresentationPresentRef = useRef(false);
  const pendingPresentationPersistenceRef = useRef<'saved' | 'unavailable' | undefined>(undefined);

  const flushPendingRounds = useCallback(() => {
    if (pendingRoundsRef.current.length === 0) return;
    // The ref is the authoritative copy of the turn, so the updater form buys
    // nothing and costs correctness: React runs updaters at RENDER time, so a
    // batch queued just before `flushAndComplete` nulled the ref would run
    // afterwards and assign the finished turn straight back into it. Every
    // later guard that asks "is a turn still live?" then read a resurrected
    // one — including the ones that decide whether to commit the answer.
    const current = liveTurnRef.current;
    if (!current) return;
    const snapshot = pendingRoundsRef.current.map((r) => ({ ...r }));
    const updated = { ...current, rounds: snapshot };
    liveTurnRef.current = updated;
    setLiveTurn(updated);
  }, []);

  const roundsBatch = useRafBatch(flushPendingRounds);

  const stopActiveTurn = useCallback(() => {
    if (activeTurnAbortRef.current) {
      activeTurnAbortRef.current.abort();
      activeTurnAbortRef.current = null;
    }
    pendingAttachmentsRef.current = undefined;
  }, []);

  // Build a dspy URL, routing through the same-origin widget-proxy in embed
  // mode (dspyBaseUrl set) or hitting dspy-api directly in-app. `path` is the
  // absolute dspy path, e.g. '/api/v1/cognition/turn/stream'. The proxy base
  // carries a path prefix (/api/widget-proxy/dspy) so we concatenate rather
  // than use `new URL(absolutePath, base)`, which would discard that prefix.
  const buildDspyUrl = useCallback(
    (path: string): string =>
      dspyBaseUrl ? `${dspyBaseUrl}${path}` : new URL(path, appSettings.api.DSPY_API_URL).toString(),
    [dspyBaseUrl],
  );

  // In embed mode use a plain fetch (bearer = token, plus the widget-proxy
  // headers); there's no iron-session cookie to refresh. In-app keep the
  // existing cookie-aware refresh wrapper untouched.
  const dspyFetch = useCallback(
    (url: string, init: RequestInit = {}): Promise<Response> =>
      dspyBaseUrl
        ? fetch(url, {
            ...init,
            headers: {
              ...(init.headers as Record<string, string>),
              ...(requestHeaders ?? {}),
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          })
        : fetchDspyWithAuthRefresh(url, init, { fallbackToken: token }),
    [dspyBaseUrl, requestHeaders, token],
  );

  // Either a direct dspy-api base (in-app) or the proxy base (embed) lets the
  // session/stream calls proceed.
  const hasDspyTarget = !!dspyBaseUrl || !!appSettings.api.DSPY_API_URL;

  const endSession = useCallback(
    async (sessionId: string) => {
      if (!token || !hasDspyTarget || !sessionId) return;
      try {
        const url = buildDspyUrl(resolvedTarget.endSessionPath(sessionId));
        await dspyFetch(url, { method: 'DELETE' });
      } catch {
        // best-effort cleanup
      }
    },
    [token, hasDspyTarget, buildDspyUrl, dspyFetch, resolvedTarget],
  );

  const closeStream = useCallback(async () => {
    stopActiveTurn();
    const sessionIds = Array.from(sessionByThreadRef.current.values());
    sessionByThreadRef.current.clear();
    await Promise.all(sessionIds.map((sessionId) => endSession(sessionId)));
  }, [endSession, stopActiveTurn]);

  const ensureSession = useCallback(
    async (threadId: string) => {
      const existingSessionId = sessionByThreadRef.current.get(threadId);
      if (existingSessionId) {
        return existingSessionId;
      }

      if (!token || !entityId || !hasDspyTarget) {
        throw new Error('Missing cognition SSE configuration');
      }

      const response = await dspyFetch(buildDspyUrl(resolvedTarget.sessionPath), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(resolvedTarget.buildSessionBody(threadId)),
      });
      if (!response.ok) {
        // Typed error so callers can distinguish 403 (access denied) / 404
        // (entity missing or dspy session expired) — the Brain Team page maps
        // these to friendly full-page states.
        throw new CognitionStreamError(
          `Failed to create cognition session (${response.status})`,
          response.status,
        );
      }
      const payload = (await response.json()) as { session_id?: string };
      if (!payload?.session_id) {
        throw new Error('Session response missing session_id');
      }
      sessionByThreadRef.current.set(threadId, payload.session_id);

      return payload.session_id;
    },
    [entityId, token, hasDspyTarget, buildDspyUrl, dspyFetch, resolvedTarget],
  );

  const sendText = useCallback(
    async (threadId: string, text: string, options?: SendTextOptions) => {
      const sessionId = await ensureSession(threadId);
      stopActiveTurn();

      const turnId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const turnAttachments =
        options?.attachments && options.attachments.length > 0 ? options.attachments : undefined;
      pendingAttachmentsRef.current = turnAttachments;

      const hasAttachmentIds = Array.isArray(options?.attachmentIds) && options.attachmentIds.length > 0;
      // The BE rejects a turn with empty `text` ("Empty turn text") even when an
      // attachment is present, so an image-only / file-only send (user attaches
      // a file and hits send without typing) failed with a generic error. When
      // there's no typed prompt but an attachment is attached, substitute a
      // default instruction so the brain still processes the attachment. The same
      // value also becomes the turn's `userText` so the optimistic user bubble
      // (which renders from `liveTurn.userText` / `completedTurn.userText`) shows
      // the prompt immediately, matching what the BE persists — otherwise the
      // bubble shows the attachment with no text until a refresh.
      const effectiveText =
        text.trim().length > 0
          ? text
          : hasAttachmentIds
          ? defaultAttachmentPrompt(options?.attachmentType)
          : text;

      const nextLiveTurn = {
        id: turnId,
        userText: options?.displayText?.trim() || effectiveText,
        rounds: [] as RoundState[],
        attachments: turnAttachments,
      };
      liveTurnRef.current = nextLiveTurn;
      pendingRoundsRef.current = [];
      pendingNextStepIntentRef.current = 'none';
      pendingNextStepOptionsRef.current = undefined;
      pendingClarifyingQuestionTextRef.current = undefined;
      pendingSourcesUsedRef.current = undefined;
      pendingSourcesRef.current = undefined;
      pendingPictureRef.current = undefined;
      pendingChartRef.current = undefined;
      pendingTurnDurationMsRef.current = undefined;
      pendingSuggestedThreadTitleRef.current = undefined;
      pendingUserThreadMessageIdRef.current = undefined;
      pendingBotThreadMessageIdRef.current = undefined;
      pendingProducedAttachmentsRef.current = undefined;
      pendingRoutingRef.current = undefined;
      pendingMapFocusRef.current = undefined;
      pendingMapStoryRef.current = undefined;
      pendingStoryProjectionRef.current = undefined;
      pendingStoryProjectionDirectivePresentRef.current = false;
      pendingStorySelectionRef.current = undefined;
      pendingStorySelectionDirectivePresentRef.current = false;
      pendingTurnFailureRef.current = undefined;
      pendingInterruptedRef.current = false;
      pendingServerTurnIdRef.current = undefined;
      pendingPresentationPlanRef.current = undefined;
      pendingPresentationPresentRef.current = !!options?.presentationViewContext;
      pendingPresentationPersistenceRef.current = undefined;
      pendingToolCallsRef.current = [];
      setLiveToolCall(null);
      setLiveRouting(null);
      setLiveTurn(nextLiveTurn);

      const flushAndComplete = () => {
        // Drop any queued RAF — we're about to do a synchronous commit instead.
        roundsBatch.cancel();
        // Synchronously commit latest rounds to liveTurnRef so setCompletedTurn sees the final text.
        // flushPendingRounds queues an async setLiveTurn — using it here would leave liveTurnRef stale.
        if (liveTurnRef.current) {
          liveTurnRef.current = {
            ...liveTurnRef.current,
            rounds: pendingRoundsRef.current.map((r) => ({ ...r })),
          };
        }
        const turn = liveTurnRef.current;
        const nextStepIntent = pendingNextStepIntentRef.current;
        const nextStepOptions = pendingNextStepOptionsRef.current;
        const clarifyingQuestionText = pendingClarifyingQuestionTextRef.current;
        const sourcesUsed = pendingSourcesUsedRef.current;
        const sources = pendingSourcesRef.current;
        const picture = pendingPictureRef.current;
        const chart = pendingChartRef.current;
        const turnDurationMs = pendingTurnDurationMsRef.current;
        const suggestedThreadTitle = pendingSuggestedThreadTitleRef.current;
        const userThreadMessageId = pendingUserThreadMessageIdRef.current;
        const botThreadMessageId = pendingBotThreadMessageIdRef.current;
        const producedAttachments = pendingProducedAttachmentsRef.current;
        const routing = pendingRoutingRef.current;
        const mapFocus = pendingMapFocusRef.current;
        const mapStory = pendingMapStoryRef.current;
        const storyProjection = pendingStoryProjectionRef.current;
        const storyProjectionDirectivePresent = pendingStoryProjectionDirectivePresentRef.current;
        const storySelection = pendingStorySelectionRef.current;
        const storySelectionDirectivePresent = pendingStorySelectionDirectivePresentRef.current;
        const failure = pendingTurnFailureRef.current;
        const interrupted = pendingInterruptedRef.current;
        const serverTurnId = pendingServerTurnIdRef.current;
        const presentationPlan = pendingPresentationPlanRef.current;
        const presentationPlanDirectivePresent = pendingPresentationPresentRef.current;
        const presentationPersistence = pendingPresentationPersistenceRef.current;
        const toolCalls = pendingToolCallsRef.current.filter((t) => !t.isDuplicate);
        setCompletedTurn((prev) =>
          turn
            ? {
                id: turn.id,
                serverTurnId,
                userText: turn.userText,
                rounds: turn.rounds,
                nextStepIntent,
                nextStepOptions,
                clarifyingQuestionText,
                sourcesUsed,
                sources,
                picture,
                chart,
                turnDurationMs,
                suggestedThreadTitle,
                toolCalls: toolCalls.length > 0 ? toolCalls : undefined,
                // Carry the turn's attachments forward so the committed user
                // message keeps its image/file content after streaming ends.
                attachments: turn.attachments,
                producedAttachments,
                userThreadMessageId,
                botThreadMessageId,
                routing,
                mapFocus,
                mapStory,
                storyProjection,
                storyProjectionDirectivePresent,
                storySelection,
                storySelectionDirectivePresent,
                failure,
                ...(interrupted ? { interrupted } : {}),
                ...(presentationPlanDirectivePresent
                  ? { presentationPlan, presentationPlanDirectivePresent, presentationPersistence }
                  : {}),
              }
            : prev,
        );
        liveTurnRef.current = null;
        pendingRoundsRef.current = [];
        pendingNextStepIntentRef.current = 'none';
        pendingNextStepOptionsRef.current = undefined;
        pendingClarifyingQuestionTextRef.current = undefined;
        pendingSourcesUsedRef.current = undefined;
        pendingSourcesRef.current = undefined;
        pendingTurnDurationMsRef.current = undefined;
        pendingSuggestedThreadTitleRef.current = undefined;
        pendingUserThreadMessageIdRef.current = undefined;
        pendingBotThreadMessageIdRef.current = undefined;
        pendingProducedAttachmentsRef.current = undefined;
        pendingRoutingRef.current = undefined;
        pendingMapFocusRef.current = undefined;
        pendingMapStoryRef.current = undefined;
        pendingStoryProjectionRef.current = undefined;
        pendingStoryProjectionDirectivePresentRef.current = false;
        pendingStorySelectionRef.current = undefined;
        pendingStorySelectionDirectivePresentRef.current = false;
        pendingTurnFailureRef.current = undefined;
        pendingInterruptedRef.current = false;
        pendingServerTurnIdRef.current = undefined;
        pendingToolCallsRef.current = [];
        setLiveToolCall(null);
        setLiveRouting(null);
        setLiveTurn(null);
      };

      const body: Record<string, unknown> = {
        session_id: sessionId,
        text: effectiveText,
      };
      if (options?.presentationViewContext) {
        body.presentation_capabilities = ATLAS_PRESENTATION_CAPABILITIES;
        body.view_context = options.presentationViewContext;
      }
      if (
        options?.attachmentType &&
        Array.isArray(options.attachmentIds) &&
        options.attachmentIds.length > 0
      ) {
        body.attachment_type = options.attachmentType;
        body.attachment_ids = options.attachmentIds;
      }
      if (options?.forceWebSearch) {
        body.force_web_search = true;
      }
      if (options?.targetCognitionId) {
        body.target_cognition_id = options.targetCognitionId;
      }
      const programBuild = safeProgramBuild(options?.programBuild);
      if (programBuild) body.program_build = programBuild;
      if (options?.mapTargets?.length) {
        const safeTargets = safeBrainTeamTargets(options.mapTargets);
        if (safeTargets.length) body.map_targets = safeTargets;
      }
      if (options?.storyTargets?.length) {
        const safeStoryTargets = safeBrainTeamTargets(options.storyTargets);
        if (safeStoryTargets.length) body.story_targets = safeStoryTargets;
      }
      if (
        typeof options?.mapUniverseTotal === 'number' &&
        Number.isFinite(options.mapUniverseTotal) &&
        options.mapUniverseTotal >= 0
      ) {
        body.map_universe_total = Math.floor(options.mapUniverseTotal);
      }
      if (options?.approvedStoryCatalog?.length) {
        const safeCatalog = options.approvedStoryCatalog
          .map(toApprovedStoryCatalogItem)
          .filter((item): item is BrainTeamApprovedStoryCatalogItem => !!item)
          .slice(0, 48);
        if (safeCatalog.length) body.approved_story_catalog = safeCatalog;
      }
      if (options?.activeStorySelection) {
        const safeActiveStory = parseBrainTeamStorySelection(options.activeStorySelection);
        if (safeActiveStory) body.active_story_selection = safeActiveStory;
      }

      const controller = new AbortController();
      activeTurnAbortRef.current = controller;
      const streamUrl = buildDspyUrl(resolvedTarget.streamPath);
      const streamInit = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      };

      try {
        let response = await dspyFetch(streamUrl, streamInit);

        // Stale cached session — create a fresh one and retry once transparently.
        // With the BE Valkey SessionSnapshot fix this should rarely fire;
        // the console.warn lets us validate that the retry rate drops post-deploy.
        if (!response.ok && (response.status === 404 || response.status === 410 || response.status === 422)) {
          console.warn('[CognitionStream] stale session retry', { status: response.status, threadId });
          sessionByThreadRef.current.delete(threadId);
          const freshSessionId = await ensureSession(threadId);
          body.session_id = freshSessionId;
          response = await dspyFetch(streamUrl, {
            ...streamInit,
            body: JSON.stringify(body),
          });
        }

        if (response.status === 409) {
          const body409 = (await response.json().catch(() => ({}))) as {
            code?: string;
            active_turn_id?: string;
          };
          // Clear liveTurnRef before flushAndComplete so it doesn't commit
          // an empty completedTurn for a message that was never processed.
          liveTurnRef.current = null;
          flushAndComplete();
          throw new CognitionStreamError(body409.code ?? 'turn_in_flight', 409, {
            code: body409.code,
            activeTurnId: body409.active_turn_id,
          });
        }

        if (!response.ok || !response.body) {
          throw new CognitionStreamError(
            `Failed to stream cognition turn (${response.status})`,
            response.status,
          );
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        void (async () => {
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              const frames = buffer.split('\n\n');
              buffer = frames.pop() || '';

              for (const frame of frames) {
                const payload = parseSseFrame<Record<string, unknown>>(frame);
                if (!payload) continue;
                try {
                  onEventRef.current?.(payload);
                } catch {
                  // A surface following along never breaks the turn it follows.
                }
                if (payload.type === 'picture') {
                  // The Brain drew (a spec grounded in its own sources, scrubbed by the runner):
                  // attach it to the live turn now, and carry it into the completed turn.
                  const picture = parseBrainPicture(payload);
                  if (picture) {
                    pendingPictureRef.current = picture;
                    const current = liveTurnRef.current;
                    if (current) {
                      const updated = { ...current, picture };
                      liveTurnRef.current = updated;
                      setLiveTurn(updated);
                    }
                  }
                  continue;
                }
                if (payload.type === 'chart') {
                  // The Brain charted (numbers its runner computed or checked): attach it to the
                  // live turn now, and carry it into the completed turn.
                  const chart = parseBrainChart(payload);
                  if (chart) {
                    pendingChartRef.current = chart;
                    const current = liveTurnRef.current;
                    if (current) {
                      const updated = { ...current, chart };
                      liveTurnRef.current = updated;
                      setLiveTurn(updated);
                    }
                  }
                  continue;
                }
                if (payload.type === 'member_routed') {
                  // Brain Team only — first event of a turn: which member brain
                  // will answer. v1 UI keeps the team voice; this powers the
                  // pre-first-token routing shimmer + the debug panel.
                  const info: TurnRoutingInfo = {
                    cognitionId: typeof payload.cognition_id === 'string' ? payload.cognition_id : undefined,
                    cognitionName:
                      typeof payload.cognition_name === 'string' ? payload.cognition_name : undefined,
                    isTeamLeader: payload.is_team_leader === true,
                    routingStage:
                      typeof payload.routing_stage === 'string' ? payload.routing_stage : undefined,
                    routingReason:
                      typeof payload.routing_reason === 'string' ? payload.routing_reason : undefined,
                    routingConfidence:
                      typeof payload.routing_confidence === 'number' ? payload.routing_confidence : undefined,
                  };
                  pendingRoutingRef.current = info;
                  setLiveRouting(info);
                  continue;
                }
                if (payload.type === 'transcript' && payload.channel === 'model') {
                  const delta = typeof payload.text === 'string' ? payload.text : '';
                  if (!delta) continue;
                  const roundIndex = typeof payload.round === 'number' ? payload.round : 0;
                  while (pendingRoundsRef.current.length <= roundIndex) {
                    pendingRoundsRef.current.push({ round: pendingRoundsRef.current.length, text: '' });
                  }
                  pendingRoundsRef.current[roundIndex].text += delta;
                  roundsBatch.schedule();
                  continue;
                }
                if (payload.type === 'round_complete') {
                  const roundIndex = typeof payload.round === 'number' ? payload.round : 0;
                  const kind =
                    payload.kind === 'thinking' || payload.kind === 'answer' ? payload.kind : 'answer';
                  if (roundIndex < pendingRoundsRef.current.length) {
                    pendingRoundsRef.current[roundIndex] = {
                      ...pendingRoundsRef.current[roundIndex],
                      kind,
                    };
                    roundsBatch.schedule();
                  }
                  continue;
                }
                if (payload.type === 'tool_call_started') {
                  const toolName = typeof payload.name === 'string' ? payload.name : '';
                  if (SKIP_TOOLS.has(toolName)) continue;
                  const toolArgs = (payload.args ?? {}) as Record<string, unknown>;
                  const toolLabel = resolveToolLabel(
                    toolName,
                    toolArgs,
                    typeof payload.display_name === 'string' ? payload.display_name : undefined,
                  );
                  const argsSummary = toolArgsSummary(toolName, toolArgs);
                  pendingToolCallsRef.current.push({
                    name: toolName,
                    label: toolLabel || toolName,
                    argsSummary,
                    resultSummary: '',
                    isError: false,
                    isDuplicate: false,
                    // The sandbox run's code rides on the start event; keep it so the
                    // trail can show exactly what ran.
                    ...(toolName === 'execute_python' && typeof toolArgs.code === 'string'
                      ? { code: toolArgs.code }
                      : {}),
                  });
                  if (toolLabel) setLiveToolCall({ name: toolName, label: toolLabel });
                  continue;
                }
                if (payload.type === 'tool_call') {
                  const toolName = typeof payload.name === 'string' ? payload.name : '';
                  if (SKIP_TOOLS.has(toolName)) continue;
                  const calls = pendingToolCallsRef.current;
                  const lastIdx = calls.length - 1;
                  if (lastIdx >= 0 && calls[lastIdx].name === toolName) {
                    const { summary, isError, isDuplicate, progress } = toolResultSummary(
                      toolName,
                      payload.result,
                    );
                    const output =
                      toolName === 'execute_python' ? executePythonOutput(payload.result) : undefined;
                    const errorText =
                      toolName === 'execute_python' ? executePythonError(payload.result) : undefined;
                    calls[lastIdx] = {
                      ...calls[lastIdx],
                      resultSummary: summary,
                      isError,
                      isDuplicate,
                      ...(output ? { output } : {}),
                      ...(errorText ? { errorText } : {}),
                    };
                    if (progress !== undefined) {
                      setLiveToolCall((prev) => {
                        if (!prev || prev.progress === progress) return prev;
                        return { ...prev, progress };
                      });
                    }
                  }
                  continue;
                }
                if (payload.type === 'turn_complete') {
                  // Correlate the plan with its terminal envelope, never with
                  // the browser's optimistic ID or the plan's own claimed ID.
                  pendingServerTurnIdRef.current =
                    typeof payload.turn_id === 'string' &&
                    /^[A-Za-z0-9][A-Za-z0-9:._-]{0,191}$/.test(payload.turn_id)
                      ? payload.turn_id
                      : undefined;
                  const presentation = presentationDirective(payload);
                  const ownsPresentation = pendingPresentationPresentRef.current || presentation.present;
                  pendingPresentationPresentRef.current = ownsPresentation;
                  pendingPresentationPlanRef.current = presentation.plan ?? undefined;
                  pendingPresentationPersistenceRef.current =
                    payload.presentation_persistence === 'saved'
                      ? 'saved'
                      : ownsPresentation
                      ? 'unavailable'
                      : undefined;
                  const terminalFailure = parseCognitionTurnFailure(payload);
                  if (terminalFailure) pendingTurnFailureRef.current = terminalFailure;
                  const turnFailed = !!pendingTurnFailureRef.current;
                  if (turnFailed) pendingPresentationPlanRef.current = undefined;
                  const rawIntent = payload.next_step_intent;
                  pendingNextStepIntentRef.current = turnFailed
                    ? 'none'
                    : rawIntent === 'clarify' || rawIntent === 'explore'
                    ? rawIntent
                    : 'none';
                  pendingNextStepOptionsRef.current =
                    !turnFailed && Array.isArray(payload.next_step_options)
                      ? (
                          payload.next_step_options as Array<{
                            label: string;
                            prompt: string;
                            chip_id?: string;
                            kind?: string;
                            intent?: string;
                            intent_category?: string;
                            is_recommended?: boolean;
                            preview?: string;
                            target_cognition_id?: string;
                            target_cognition_name?: string;
                            map_focus?: unknown;
                          }>
                        )
                          .filter((opt) => typeof opt === 'object' && opt !== null)
                          .map((opt, i) => {
                            // Validate BE-shipped intent_category against the
                            // closed enum. Unknown values (typos, future BE
                            // additions FE hasn't typed yet) drop to null →
                            // FE falls back to verb-derived bucketing on the
                            // label. Defensive on the FE side too even though
                            // BE parser strict-validates — keeps the wire
                            // schema as the source of truth.
                            const cat =
                              opt.intent_category === 'synthesize' ||
                              opt.intent_category === 'probe' ||
                              opt.intent_category === 'deepen' ||
                              opt.intent_category === 'apply' ||
                              opt.intent_category === 'clarify' ||
                              opt.intent_category === 'other'
                                ? opt.intent_category
                                : undefined;
                            return {
                              chip_id: typeof opt.chip_id === 'string' ? opt.chip_id : `opt-${i}`,
                              label: typeof opt.label === 'string' ? opt.label : '',
                              prompt: typeof opt.prompt === 'string' ? opt.prompt : '',
                              kind:
                                opt.kind === 'clarifying' || opt.kind === 'follow_up' ? opt.kind : undefined,
                              // BE ships these on block-directions today;
                              // next_step_options from cognition turn_complete
                              // currently land with them absent until those
                              // surfaces graduate to the same query-gen
                              // architecture. Forward when present so the
                              // card-style rail consumes them as soon as they
                              // start populating.
                              ...(typeof opt.intent === 'string' && opt.intent.length > 0
                                ? { intent: opt.intent }
                                : {}),
                              ...(cat ? { intent_category: cat } : {}),
                              ...(typeof opt.is_recommended === 'boolean'
                                ? { is_recommended: opt.is_recommended }
                                : {}),
                              ...(typeof opt.preview === 'string' && opt.preview.length > 0
                                ? { preview: opt.preview }
                                : {}),
                              ...(typeof opt.target_cognition_id === 'string' && opt.target_cognition_id
                                ? {
                                    target_cognition_id: opt.target_cognition_id,
                                    ...(typeof opt.target_cognition_name === 'string'
                                      ? { target_cognition_name: opt.target_cognition_name }
                                      : {}),
                                  }
                                : {}),
                              ...(!ownsPresentation && parseBrainTeamMapFocus(opt.map_focus)
                                ? { map_focus: parseBrainTeamMapFocus(opt.map_focus) }
                                : {}),
                            };
                          })
                          .filter((opt) => opt.label && opt.prompt)
                      : undefined;
                  pendingClarifyingQuestionTextRef.current =
                    typeof payload.clarifying_question_text === 'string'
                      ? payload.clarifying_question_text
                      : undefined;
                  pendingMapFocusRef.current =
                    turnFailed || ownsPresentation ? undefined : parseBrainTeamMapFocus(payload.map_focus);
                  const hasTerminalStoryProjection = hasBrainTeamStoryProjectionDirective(
                    payload.story_projection,
                  );
                  const terminalStoryProjection =
                    turnFailed || ownsPresentation
                      ? undefined
                      : parseBrainTeamStoryProjection(payload.story_projection);
                  pendingStoryProjectionRef.current = terminalStoryProjection;
                  pendingStoryProjectionDirectivePresentRef.current =
                    !ownsPresentation && hasTerminalStoryProjection;
                  const rawStorySelection = payload.storySelection ?? payload.story_selection;
                  const terminalStorySelection = classifyBrainTeamStorySelectionDirective(rawStorySelection);
                  const hasTerminalStorySelection = terminalStorySelection.kind !== 'absent';
                  pendingStorySelectionDirectivePresentRef.current =
                    !ownsPresentation && hasTerminalStorySelection;
                  pendingStorySelectionRef.current =
                    turnFailed || ownsPresentation
                      ? undefined
                      : terminalStorySelection.kind === 'selection'
                      ? terminalStorySelection.selection
                      : undefined;
                  // The versioned projection is authoritative. `map_story`
                  // remains a legacy fallback for older completed turns, never
                  // a second same-turn player with its own timer and camera.
                  pendingMapStoryRef.current =
                    turnFailed || ownsPresentation || hasTerminalStoryProjection || hasTerminalStorySelection
                      ? undefined
                      : parseBrainTeamMapStory(payload.map_story);
                  pendingSourcesUsedRef.current = Array.isArray(payload.sources_used)
                    ? (payload.sources_used as string[]).filter(
                        (s): s is 'kb' | 'web' | 'attachment' | 'general' =>
                          s === 'kb' || s === 'web' || s === 'attachment' || s === 'general',
                      )
                    : undefined;
                  pendingSourcesRef.current = parseTurnSources(payload.sources);
                  if (!pendingPictureRef.current) {
                    pendingPictureRef.current = parseBrainPicture(payload.picture) ?? undefined;
                  }
                  if (!pendingChartRef.current) {
                    pendingChartRef.current = parseBrainChart(payload.chart) ?? undefined;
                  }
                  pendingTurnDurationMsRef.current =
                    typeof payload.turn_duration_ms === 'number' ? payload.turn_duration_ms : undefined;
                  if (typeof payload.user_thread_message_id === 'string') {
                    pendingUserThreadMessageIdRef.current = payload.user_thread_message_id;
                    if (pendingAttachmentsRef.current && pendingAttachmentsRef.current.length > 0) {
                      attachmentsByMessageIdRef.current.set(
                        payload.user_thread_message_id,
                        pendingAttachmentsRef.current,
                      );
                      setAttachmentsVersion((v) => v + 1);
                    }
                  }
                  const botThreadMessageId =
                    typeof payload.thread_message_id === 'string'
                      ? payload.thread_message_id
                      : typeof payload.bot_thread_message_id === 'string'
                      ? payload.bot_thread_message_id
                      : typeof payload.ai_message_id === 'string'
                      ? payload.ai_message_id
                      : undefined;
                  if (botThreadMessageId) {
                    pendingBotThreadMessageIdRef.current = botThreadMessageId;
                  }
                  pendingAttachmentsRef.current = undefined;
                  // What the run produced: the engine attaches execute_python's file to the reply and
                  // lists it here, so the committed answer can show it without a reload.
                  const producedAttachments = parseProducedAttachments(payload.attachments);
                  pendingProducedAttachmentsRef.current =
                    producedAttachments.length > 0 ? producedAttachments : undefined;
                  pendingSuggestedThreadTitleRef.current =
                    typeof payload.thread_title_suggestion === 'string' &&
                    payload.thread_title_suggestion.length > 0
                      ? payload.thread_title_suggestion
                      : undefined;
                  // Brain Team — turn_complete carries the authoritative
                  // delegated member. Merge over the member_routed snapshot
                  // (which may be missing if the event raced the reconnect).
                  const delegatedCognitionId =
                    typeof payload.delegated_cognition_id === 'string'
                      ? payload.delegated_cognition_id
                      : undefined;
                  const delegatedCognitionName =
                    typeof payload.delegated_cognition_name === 'string'
                      ? payload.delegated_cognition_name
                      : undefined;
                  if (delegatedCognitionId || delegatedCognitionName) {
                    pendingRoutingRef.current = {
                      ...(pendingRoutingRef.current ?? {}),
                      cognitionId: delegatedCognitionId ?? pendingRoutingRef.current?.cognitionId,
                      cognitionName: delegatedCognitionName ?? pendingRoutingRef.current?.cognitionName,
                      routingStage:
                        typeof payload.routing_stage === 'string'
                          ? payload.routing_stage
                          : pendingRoutingRef.current?.routingStage,
                    };
                  }
                  flushAndComplete();
                  return;
                }
                if (payload.type === 'error') {
                  pendingTurnFailureRef.current = parseCognitionTurnFailure(payload) ?? {
                    message:
                      typeof payload.message === 'string'
                        ? payload.message
                        : 'This answer could not be completed. Please try again.',
                    retryable: true,
                  };
                  // Some backends follow an error event with the authoritative
                  // turn_complete ids. Keep reading so that terminal can commit
                  // the row, but never let it erase the failure recorded above.
                  continue;
                }
              }
            }
            // The stream closed without its terminal frame: unless the server said what went
            // wrong, the connection broke under a turn that may still be running.
            if (!pendingTurnFailureRef.current) pendingInterruptedRef.current = true;
            flushAndComplete();
          } catch (error) {
            if (controller.signal.aborted) return;
            console.error('Failed while processing cognition SSE stream:', error);
            if (!pendingTurnFailureRef.current) pendingInterruptedRef.current = true;
            pendingTurnFailureRef.current ??= {
              message: 'This answer could not be completed. Please try again.',
              retryable: true,
            };
            flushAndComplete();
          } finally {
            if (activeTurnAbortRef.current === controller) activeTurnAbortRef.current = null;
          }
        })();

        return turnId;
      } catch (err) {
        if (liveTurnRef.current && !(err instanceof CognitionStreamError && err.httpStatus === 409)) {
          pendingTurnFailureRef.current ??= {
            message: 'This answer could not be completed. Please try again.',
            retryable: true,
          };
        }
        flushAndComplete();
        throw err;
      }
    },
    [ensureSession, roundsBatch, stopActiveTurn, buildDspyUrl, dspyFetch, resolvedTarget],
  );

  // Teardown belongs to the unmount, not to the callback's identity.
  //
  // `closeStream` is rebuilt whenever the session token changes (it closes over
  // `dspyFetch`, which closes over `token`), and with `[closeStream]` as the
  // dependency that rebuild ran the CLEANUP: mid-turn, a token rotation aborted
  // the in-flight SSE and deleted every dspy session. The aborted reader takes
  // the silent `return` path and never commits, so the answer that was
  // streaming is discarded on the client while the backend still persists it —
  // gone from the screen, back after a refresh.
  const closeStreamRef = useRef(closeStream);
  useEffect(() => {
    closeStreamRef.current = closeStream;
  }, [closeStream]);
  useEffect(
    () => () => {
      void closeStreamRef.current();
    },
    [],
  );

  // Reset the optimistic-attachment cache when switching entities (cognition
  // or brain team) so a long session doesn't accumulate entries from prior
  // brains/teams.
  useEffect(() => {
    attachmentsByMessageIdRef.current = new Map();
    setAttachmentsVersion((v) => v + 1);
  }, [entityId]);

  const getAttachmentsForMessage = useCallback(
    (messageId: string) => attachmentsByMessageIdRef.current.get(messageId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity tracked via attachmentsVersion
    [attachmentsVersion],
  );

  return {
    liveTurn,
    completedTurn,
    liveToolCall,
    liveRouting,
    sendText,
    closeStream,
    getAttachmentsForMessage,
  };
}
